"use client"

import { cn } from "@/lib/utils" // Assuming cn is in lib/utils
import Image from "next/image"

interface AuthorCardProps {
  className?: string
  backgroundImage?: string
  author: {
    name: string
    avatar: string
    readTime?: string
  }
  content: {
    title: string
    description: string
    isRtl?: boolean
  }
}

export const AuthorCard = ({ 
  className,
  backgroundImage,
  author,
  content
}: AuthorCardProps) => {
  const isRtl = content.isRtl || false;
  
  return (
    // Responsive width adjustment
    <div className="w-full group/card mx-auto"> 
      <div
        className={cn(
          "cursor-pointer overflow-hidden relative card h-80 sm:h-96 rounded-md shadow-xl flex flex-col justify-between p-3 sm:p-4 bg-cover bg-center",
          className
        )}
        // Use provided background image or a fallback placeholder
        style={{ backgroundImage: `url(${backgroundImage})` }}
      >
        {/* Overlay - only on hover */}
        <div className="absolute w-full h-full top-0 left-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent opacity-0 group-hover/card:opacity-80 transition-opacity duration-300" />
        
        {/* Author Info */}
        <div className="flex flex-row items-center space-x-3 sm:space-x-4 z-10">
          <Image
            height={40}
            width={40}
            alt={`${author.name}'s avatar`}
            src={author.avatar || '/placeholder.svg?text=A'} // Fallback avatar
            className="h-8 w-8 sm:h-10 sm:w-10 rounded-full border-2 border-white/50 object-cover" // Reduced size on mobile
          />
          <div className="flex flex-col">
            <p className="font-medium text-sm sm:text-base text-gray-50 relative z-10">
              {author.name}
            </p>
            {author.readTime && (
              <p className="text-xs sm:text-sm text-gray-400 relative z-10">{author.readTime}</p>
            )}
          </div>
        </div>
        
        {/* Content Info */}
        <div className="text-content z-10">
          <h3 
            className={cn(
              "font-bold text-lg sm:text-xl text-gray-50 relative whitespace-pre-line break-words",
              isRtl && "text-right direction-rtl"
            )}
            dir={isRtl ? "rtl" : "ltr"}
          >
            {content.title}
          </h3>
          {/* Introduction/description removed for clarity */}
        </div>
      </div>
    </div>
  )
} 